module Sm.Service.Database

open System
open Browser
open Browser.Types
open Fable.Core.JsInterop

open Sm.Service.App

let [<Literal>] NamePrefix = "smm"
let [<Literal>] Version = 1

let [<Literal>] RecordStore = "records"
let [<Literal>] AppStore = "app"
let [<Literal>] TrackingStore = "tracking"

let result<'T> (request: IDBRequest) =
    Promise.create (fun resolve reject ->
        request.addEventListener("success", fun _ ->
            resolve (unbox<'T> request.result)
        )

        request.addEventListener("error", fun _ ->
            reject !!request.error
        )
    )

let connect name =
    Promise.create (fun resolve reject ->
        let openRequest = indexedDB.``open``(name, Version)

        let inline requireDb () = unbox<IDBDatabase> openRequest.result

        openRequest.addEventListener("upgradeneeded", fun e ->
            let db = requireDb ()
            let oldVersion: int = e?oldVersion

            let addIndex multi field (store: IDBObjectStore) =
                let options = createEmpty<IDBCreateIndexOptions>
                if multi then options.multiEntry <- true
                store.createIndex(field, field, options)
                |> ignore

            let addSingleIndex = addIndex false
            let addMultiIndex = addIndex true

            if oldVersion < 1 then
                let options = createEmpty<IDBCreateStoreOptions>
                options.keyPath <- !!"id"
                let records = db.createObjectStore(RecordStore, options)

                records |> addSingleIndex "pending"
                records |> addSingleIndex "activity"
                records |> addSingleIndex "clock"
                records |> addMultiIndex "local"

                records.createIndex("scope", "fields.scope.value") |> ignore

                let app = db.createObjectStore(AppStore)

                let clientId = Guid.NewGuid()
                app.put(clientId, "client-id") |> result<unit> |> Promise.start

                db.createObjectStore(TrackingStore) |> ignore
        )

        openRequest.addEventListener("blocked", fun _ ->
            reloadSwitch.Post(On "A reload is required to finish updating the app")
            reject (exn "Unable to connect, version change is blocked by an existing connection")
        )

        openRequest.addEventListener("success", fun _ ->
            let result = unbox<IDBDatabase> openRequest.result
            resolve result
        )

        openRequest.addEventListener("error", fun _ ->
            reject !!openRequest.error
        )
    )

let delete name =
    let request = indexedDB.deleteDatabase(name)
    request |> result<unit>

let openStore (mode: IDBTransactionMode) name database =
    promise {
        let! db = connect database
        let tx = db.transaction(name, mode)
        return tx.objectStore(name)
    }

let readStore = openStore Readonly
let writeStore = openStore Readwrite

let readRecordStore = readStore RecordStore
let writeRecordStore = writeStore RecordStore

module KeyRange =
    let inline unbounded () =
        IDBKeyRange.bound(Double.NegativeInfinity, Double.PositiveInfinity, true, true)

    let startsWith s =
        let upper = s + "\uFFFF"
        IDBKeyRange.bound(s, upper, true, true)

module Cursor =
    let scan f (request: IDBRequest) =
        Promise.create (fun resolve reject ->
            let result = ResizeArray<_>()

            request.addEventListener("success", fun _ ->
                let cursor: IDBCursor option =  !!request.result

                match cursor with
                | None -> resolve result
                | Some cursor ->
                    match f result.Count cursor with
                    | None -> resolve result
                    | Some x ->
                        result.Add(x)
                        cursor.``continue``()
            )

            request.addEventListener("error", fun _ ->
                reject(!!request.error)
            )
        )

    let take count f request =
        request
        |> scan (fun i cursor ->
            if i < count then Some (f cursor)
            else None
        )

    let first<'T> (f: IDBCursor -> 'T) request =
        request
        |> take 1 f
        |> Promise.map (fun result ->
            if result.Count = 1 then Some (unbox<'T> result[0])
            else None
        )
